import React, { useState, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { VideoPlayerProps } from './VideoPlayer.types';

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  thumbnailUrl,
  sectionTitle = 'Film z oględzin pojazdu',
  vehicleDescription = '',
  className,
  onPlay,
  onPause,
  onEnded,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [playbackError, setPlaybackError] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setIsPlaying(false);
    setShowControls(false);
    setPlaybackError(false);
  }, [videoUrl]);

  const handlePlayClick = useCallback(async () => {
    const video = videoRef.current;

    if (!video || playbackError) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      onPause?.();
      return;
    }

    try {
      await video.play();
      setIsPlaying(true);
      setShowControls(true);
      onPlay?.();
    } catch {
      setPlaybackError(true);
    }
  }, [isPlaying, onPlay, onPause, playbackError]);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    setShowControls(false);
    onEnded?.();
  }, [onEnded]);

  const handleVideoError = useCallback(() => {
    setIsPlaying(false);
    setShowControls(false);
    setPlaybackError(true);
  }, []);

  const handleVideoClick = useCallback(() => {
    void handlePlayClick();
  }, [handlePlayClick]);

  return (
    <div className={clsx('relative w-full', className)}>
      {/* Video Container */}
      <div className="relative w-full overflow-hidden rounded-[15px]">
        {/* Video Element */}
        <video
          ref={videoRef}
          src={videoUrl}
          poster={thumbnailUrl}
          className={clsx(
            'aspect-video w-full object-cover',
            !playbackError && 'cursor-pointer',
          )}
          onClick={!playbackError ? handleVideoClick : undefined}
          onEnded={handleVideoEnded}
          onError={handleVideoError}
          controls={showControls && isPlaying}
          playsInline
        />

        {/* Overlay - widoczny gdy nie odtwarzamy */}
        {!isPlaying && (
          <>
            {/* Dark overlay */}
            <div
              className={clsx(
                'absolute inset-0 rounded-[15px] bg-[rgba(32,38,42,0.81)]',
                !playbackError && 'cursor-pointer',
              )}
              onClick={!playbackError ? handleVideoClick : undefined}
            />

            {playbackError ? (
              <div className="absolute left-1/2 top-1/2 w-full max-w-[80%] -translate-x-1/2 -translate-y-1/2 text-center">
                <p
                  className="text-sm font-medium text-white"
                  aria-live="polite"
                >
                  Nie udało się odtworzyć tego filmu. Pobierz film, aby go
                  obejrzeć.
                </p>
              </div>
            ) : (
              <button
                onClick={handleVideoClick}
                className="absolute left-1/2 top-1/2 flex h-[88px] w-[88px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
                aria-label="Odtwórz film"
              >
                <svg
                  width="43"
                  height="49"
                  viewBox="0 0 43 49"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="ml-1"
                >
                  <path
                    d="M40.477 23.6602C40.81 23.8527 40.81 24.3339 40.477 24.5264L2.25049 46.6143C1.91715 46.8069 1.49951 46.5666 1.49951 46.1816L1.49951 2.00488C1.49951 1.6199 1.91716 1.37868 2.25049 1.57129L40.477 23.6602Z"
                    stroke="white"
                    strokeWidth="3"
                  />
                </svg>
              </button>
            )}
          </>
        )}

        {/* Footer bar — ukryty podczas odtwarzania, aby nie zasłaniał natywnego
            paska sterowania (przewijania) playera */}
        {!isPlaying && (
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between rounded-bl-[15px] rounded-br-[15px] bg-text-primary px-6 py-3">
            <span className="text-[12px] font-bold uppercase tracking-[0.5px] text-[#d4d4d4]">
              {sectionTitle}
            </span>
            {vehicleDescription && (
              <span className="text-[12px] font-light tracking-[0.5px] text-[#d4d4d4]">
                {vehicleDescription}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

VideoPlayer.displayName = 'VideoPlayer';
